import React from "react";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const reported = (value) => typeof value === "number" && Number.isFinite(value);

export const gaugeColor = (value, limits = {}) => {
  if ((reported(limits.lsl) && value < limits.lsl) || (reported(limits.usl) && value > limits.usl)) return "#f43f5e";
  if ((reported(limits.lcl) && value < limits.lcl) || (reported(limits.ucl) && value > limits.ucl)) return "#eab308";
  return reported(limits.lcl) && reported(limits.ucl) ? "#16b96b" : "#64748b";
};

/** Gauge zones come from the recorded specification and control limits. */
const GaugeDial = ({ label, value, unit = "", min, max, limits = {}, size = 260 }) => {
  const startAngle = 180;
  const sweep = 180;
  const radius = size / 2 - 30;
  const cx = size / 2;
  const cy = size / 2 + 18;
  const angleFor = (reading) => startAngle + clamp((reading - min) / (max - min || 1), 0, 1) * sweep;
  const toXY = (deg, r) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const arcPath = (fromDeg, toDeg, r) => {
    const [x1, y1] = toXY(fromDeg, r);
    const [x2, y2] = toXY(toDeg, r);
    return `M ${x1} ${y1} A ${r} ${r} 0 ${toDeg - fromDeg > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };
  const bounds = [...new Set([min, max, ...Object.values(limits).filter((limit) => reported(limit) && limit > min && limit < max)])].sort((first, second) => first - second);
  const markerColor = gaugeColor(value, limits);
  const [markerX, markerY] = toXY(angleFor(value), radius);
  const ticks = Array.from({ length: 9 }, (_, index) => ({
    angle: startAngle + (sweep * index) / 8,
    value: min + ((max - min) * index) / 8,
  }));

  return (
    <div className="flex flex-col items-center">
      <svg className="h-auto w-full max-w-[280px]" viewBox={`0 0 ${size} ${size * 0.68}`} role="img" aria-label={`${label}: ${value}${unit}`}>
        {bounds.slice(1).map((upper, index) => <path key={upper} d={arcPath(angleFor(bounds[index]), angleFor(upper), radius)} fill="none" stroke={gaugeColor((bounds[index] + upper) / 2, limits)} strokeWidth={4} />)}
        {ticks.map((tick, index) => {
          const [x1, y1] = toXY(tick.angle, radius - 12);
          const [x2, y2] = toXY(tick.angle, radius - 25);
          const [tx, ty] = toXY(tick.angle, radius + 16);
          return <g key={index}><line x1={x1} y1={y1} x2={x2} y2={y2} stroke={gaugeColor(tick.value, limits)} strokeWidth="2"/><text x={tx} y={ty} fontSize="10" fill="#9ca3af" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${tick.angle + 90} ${tx} ${ty})`}>{Number.isInteger(tick.value) ? tick.value : tick.value.toFixed(1)}</text></g>;
        })}
        <circle cx={markerX} cy={markerY} r="6" fill={markerColor} stroke="#fff" strokeWidth="2" />
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="24" fontWeight="700" fill={markerColor}>{value}</text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize="12" fill="#6b7280">{label} ({unit})</text>
      </svg>
    </div>
  );
};

export default GaugeDial;
